import {PlusCircleOutlined} from '@ant-design/icons';
import {Form, Input, Tooltip, Badge, Button} from 'antd';
import cn from 'classnames';
import {xor} from 'lodash-es';
import React, {useState, FC, useEffect, useRef} from 'react';

import {CheckboxWithToggle} from 'components/@common/specific/сheckbox-with-toggle/checkbox-with-toggle';
import {CustomSelect, ECustomSelectMode} from 'components/form/inputs/custom-select';
import {
    IGroupCompanyOperations,
    IDependentPartListItem,
    IOption,
} from 'pages/ais/organization-structure-v5/model/types';

import {DependentPartConditions} from './dependent-part-conditions';
import styles from './dependent-part-list-item.module.less';

export const DependentPartListLitem: FC<IDependentPartListItem> = ({
    form,
    setSelectedRows,
    selectedRows,
    field,
}: IDependentPartListItem) => {
    const [isParticipantNameRusRequreid, setisParticipantNameRusRequreid] = useState<boolean>(false);

    const [fieldsCount, setFieldsCount] = useState<number | undefined>(0);

    const conditionsRef = useRef<IGroupCompanyOperations | null>(null);

    const [areConditionsHidden, setAreConditionsHidden] = useState(true);

    const participantType = form.getFieldValue(['companyGroupParticipantInfoDtos', field.name, 'participantType']);

    const companyGroupShareParentInfoDtos = form.getFieldValue([
        'companyGroupParticipantInfoDtos',
        field.name,
        'companyGroupShareParentInfoDtos',
    ]);

    useEffect(() => {
        if (participantType && participantType?.description) {
            const description = participantType.description || participantType;
            const isPartName = description === 'российская организация';
            setisParticipantNameRusRequreid(isPartName);
        }
    }, [participantType]);

    useEffect(() => {
        if (companyGroupShareParentInfoDtos && companyGroupShareParentInfoDtos?.length !== 0) {
            setFieldsCount(companyGroupShareParentInfoDtos.length);
            setAreConditionsHidden(false);
        }
    }, [companyGroupShareParentInfoDtos]);

    const handleChange = (_: any, option: IOption) => {
        setisParticipantNameRusRequreid(option.children === 'российская организация');
    };

    return (
        <article
            key={field.name}
            className={styles['dependent-part-list__row']}
        >
            <>
                <section className={styles['dependent-part-list__top']}>
                    <CheckboxWithToggle
                        selected={selectedRows.includes(field.name)}
                        onToggleSelect={() => setSelectedRows(xor(selectedRows, [field.name]))}
                        fieldsCount={fieldsCount}
                        areConditionsHidden={areConditionsHidden}
                        setAreConditionsHidden={setAreConditionsHidden}
                    />
                    <section className="d-flex flex-column">
                        <div className="d-flex gap-2">
                            <Form.Item
                                rules={[{required: true}]}
                                label="Тип подчиненности участника к организации, представляющей сведения"
                                name={[field.name, 'subordination']}
                                className="w-100"
                            >
                                <CustomSelect
                                    settings={{
                                        url: 'valueLists/FLEX_VALUE?sublistCode=subordination',
                                        valuePath: 'id',
                                        labelPath: 'description',
                                    }}
                                />
                            </Form.Item>
                        </div>
                        <Form.Item
                            rules={[{required: true}]}
                            label="Признак взаимозависимости с организацией, представляющей сведения, в соответствии с п.2 ст.105.1 НК РФ"
                            name={[field.name, 'interdependence']}
                        >
                            <CustomSelect
                                settings={{
                                    mode: ECustomSelectMode.multiple,
                                    showSearch: true,
                                    url: 'valueLists/FLEX_VALUE?sublistCode=interdependence',
                                    valuePath: 'id',
                                    labelPath: 'description',
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            rules={[{required: true}]}
                            label="Признак участника группы"
                            name={[field.name, 'participantType']}
                        >
                            <CustomSelect
                                onChange={handleChange}
                                settings={{
                                    url: 'valueLists/FLEX_VALUE?sublistCode=participant_type',
                                    valuePath: 'id',
                                    labelPath: 'description',
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            rules={[
                                {required: isParticipantNameRusRequreid},
                                {pattern: /^.{1,250}$/, message: 'Количество символов должно быть не более 250'},
                            ]}
                            validateTrigger="onBlur"
                            label="Наименование организации участника группы компаний: материнской или дочерней (зависимой) организации для организации, представляющей сведения (в русской транскрипции)"
                            name={[field.name, 'participantName']}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            rules={[
                                {required: !isParticipantNameRusRequreid},
                                {pattern: /^.{1,250}$/, message: 'Количество символов должно быть не более 250'},
                            ]}
                            validateTrigger="onBlur"
                            label="Наименование организации участника группы компаний: материнской или дочерней (зависимой) организации для организации, представляющей сведения (в латинской транскрипции)"
                            name={[field.name, 'participantNameLat']}
                        >
                            <Input />
                        </Form.Item>
                        <div className="d-flex gap-2">
                            <Form.Item
                                label="ИНН материнской или дочерней (зависимой) организации"
                                rules={[
                                    {required: isParticipantNameRusRequreid},
                                    {pattern: /^\d{10}$/, message: 'Количество цифр должно быть от 10'},
                                ]}
                                validateTrigger="onBlur"
                                name={[field.name, 'participantInn']}
                                className={styles['dependent-part-list__participant-inn--width']}
                            >
                                <Input
                                    onKeyPress={(e) => {
                                        if (!/[0-9]/.test(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                />
                            </Form.Item>
                            <Form.Item
                                label="ОГРН"
                                rules={[
                                    {required: isParticipantNameRusRequreid},
                                    {pattern: /^\d{13}$/, message: 'Количество цифр должно быть равное 13'},
                                ]}
                                validateTrigger="onBlur"
                                name={[field.name, 'ogrn']}
                                className={styles['dependent-part-list__ogrn--width']}
                            >
                                <Input
                                    onKeyPress={(e) => {
                                        if (!/[0-9]/.test(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                />
                            </Form.Item>
                        </div>
                        <Form.Item
                            rules={[
                                {required: false},
                                {pattern: /^.{1,100}$/, message: 'Количество символов должно быть не более 100'},
                            ]}
                            validateTrigger="onBlur"
                            label="Регистрационный номер материнской или дочерней (зависимой) организации в стране регистрации (инкорпорации)"
                            name={[field.name, 'foreignRegNumber']}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            label="Код страны регистрации материнской или дочерней (зависимой) организации (по справочнику ОКСМ), числовое значение"
                            rules={[
                                {required: !isParticipantNameRusRequreid},
                                {pattern: /^\d{3}$/, message: 'Количество цифр должно быть равным 3'},
                            ]}
                            validateTrigger="onBlur"
                            name={[field.name, 'countryCode']}
                        >
                            <Input
                                onKeyPress={(e) => {
                                    if (!/[0-9]/.test(e.key)) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        </Form.Item>
                        <div className={styles['dependent-part-list__participant-group']}>
                            <Form.Item
                                label="Код группы компаний (холдинга), к которой относится участник/дочерняя (зависимая) организация"
                                rules={[
                                    {required: true},
                                    {
                                        pattern: /^(\d{1,3})(,\d{1,3})*$/,
                                        message:
                                            'Введите допустимые значения. Принимаются числовые значения от 1 до 3 знаков через запятую без пробелов',
                                    },
                                ]}
                                validateTrigger="onBlur"
                                name={[field.name, 'participantGroupCode']}
                                className={styles['dependent-part-list__participant-group-code--width']}
                            >
                                <Input
                                    onKeyPress={(e) => {
                                        if (!/[0-9,]/.test(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                />
                            </Form.Item>
                            <Form.Item label={<>&nbsp;</>}>
                                <Tooltip
                                    title="Добавить сведения о доле участника в капитале"
                                    placement="top"
                                    mouseEnterDelay={0.8}
                                >
                                    <Badge
                                        count={fieldsCount}
                                        size="small"
                                    >
                                        <Button
                                            type="primary"
                                            className="btn-only-icon"
                                            onClick={() => {
                                                conditionsRef.current?.add();
                                                setFieldsCount((p) => (p ?? 0) + 1);
                                                setAreConditionsHidden(false);
                                            }}
                                        >
                                            <PlusCircleOutlined />
                                        </Button>
                                    </Badge>
                                </Tooltip>
                            </Form.Item>
                        </div>
                    </section>
                </section>
                <section className={styles['dependent-part-list__bottom']}>
                    <div
                        className={cn({
                            [styles['dependent-part-list__bottom--line']]: !areConditionsHidden && !(fieldsCount === 0),
                        })}
                    />
                    <div className={cn({[styles['dependent-part-list__conditions_hidden']]: areConditionsHidden})}>
                        <Form.List name={[field.name, 'companyGroupShareParentInfoDtos']}>
                            {(conditionsFields, conditionsListOperations) => (
                                <DependentPartConditions
                                    ref={conditionsRef}
                                    conditionsFields={conditionsFields}
                                    conditionsListOperations={conditionsListOperations}
                                    setFieldsCount={setFieldsCount}
                                />
                            )}
                        </Form.List>
                    </div>
                </section>
            </>
        </article>
    );
};
